/**
 * AddStationForm Component
 * Form for adding new stations
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AddStationForm = ({
  newStation,
  setNewStation,
  testResult,
  testing,
  fingerprinting,
  onTest,
  onFingerprint,
  onAdd,
  onCancel
}) => {
  return (
    <Card className="bg-gray-100 dark:bg-gray-800/80 mb-4 border border-gray-200 dark:border-gray-700">
      <CardContent className="pt-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Προσθήκη Νέου Σταθμού
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            value={newStation.name}
            onChange={(e) => setNewStation({ ...newStation, name: e.target.value })}
            placeholder="Όνομα σταθμού"
            className="text-sm"
          />
          <Input
            value={newStation.description}
            onChange={(e) => setNewStation({ ...newStation, description: e.target.value })}
            placeholder="Περιγραφή (προαιρετικό)"
            className="text-sm"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🎵 Stream URL <span className="text-blue-500">(για ICY metadata)</span>
            </label>
            <Input
              value={newStation.stream_url}
              onChange={(e) => setNewStation({ ...newStation, stream_url: e.target.value })}
              placeholder="https://stream..."
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🌐 Website URL <span className="text-cyan-500">(auto-detect scraping)</span>
            </label>
            <Input
              data-testid="add-station-website-url"
              value={newStation.website_url || ''}
              onChange={(e) => setNewStation({ ...newStation, website_url: e.target.value })}
              placeholder="https://fystikiradio.gr/"
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🔗 Metadata API <span className="text-orange-500">(αντί για stream - προαιρετικό)</span>
            </label>
            <Input
              value={newStation.metadata_api_url || ''}
              onChange={(e) => setNewStation({ ...newStation, metadata_api_url: e.target.value })}
              placeholder="https://api... (custom metadata endpoint)"
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              📊 Stats URL <span className="text-green-500">(για listeners - προαιρετικό)</span>
            </label>
            <Input
              value={newStation.stats_url || ''}
              onChange={(e) => setNewStation({ ...newStation, stats_url: e.target.value })}
              placeholder="https://.../status-json.xsl ή /stats"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              👥 Listeners API <span className="text-purple-500">(εναλλακτικό - προαιρετικό)</span>
            </label>
            <Input
              value={newStation.listeners_api_url || ''}
              onChange={(e) => setNewStation({ ...newStation, listeners_api_url: e.target.value })}
              placeholder="https://api.../listeners"
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 items-center">
          <Button 
            onClick={onTest} 
            disabled={testing || fingerprinting} 
            size="sm" 
            variant="outline"
          >
            {testing ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
            Δοκιμή URL
          </Button>
          <Button 
            onClick={onFingerprint} 
            disabled={testing || fingerprinting} 
            size="sm" 
            variant="outline" 
            className="bg-purple-500/20 border-purple-500/50"
          >
            {fingerprinting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : '🎤'}
            Fingerprint
          </Button>
          
          {testResult && (
            <span className={`text-sm ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {testResult.success ? (
                <>✅ {testResult.metadata?.title}</>
              ) : (
                <>❌ {testResult.error}</>
              )}
            </span>
          )}
          
          <div className="flex-1" />
          
          <Button onClick={onCancel} size="sm" variant="ghost">
            Ακύρωση
          </Button>
          <Button onClick={onAdd} disabled={!testResult?.success && !newStation.metadata_api_url} size="sm">
            Προσθήκη
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddStationForm;
